<template>
  <DisplayPluginOptionsAPIProvider>
    <ThemeOptionsAPIProvider>
      <v-app>
        <layout>
          <router-view />
        </layout>
        <v-snackbar
          v-model="visible"
          :timeout="snackbar?.timeout || 5000"
          :color="snackbar?.color"
          location="top"
          data-cy="snack-content"
        >
          {{ snackbar?.message }}
          <template #actions="{ attrs }">
            <v-btn
              icon
              v-bind="attrs"
              @click="removeSnackbar"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </v-app>
    </ThemeOptionsAPIProvider>
  </DisplayPluginOptionsAPIProvider>
</template>

<script>
import config from './configs'
import Layout from '@/layouts/Layout'
import {
  getGlobalSettingByIdUsingGET as getSingleSetting,
  getProcessByIdUsingGET as getProcess,
  getGlobalSettingsUsingGET as getSettings,
  getCurrentUserInfoUsingGET as getUser,
  getGlobalSettingBasicUsingGET as getBasicSettings
} from '@/utils/api'
import { getBuildInfoUsingGET as getBuildInfo } from '@/utils/api'
import { RTL_LANGUAGES } from '@/utils/constants'
import { mapActions, mapGetters } from 'vuex'
import axios from 'axios'
import auth from '@/auth/index'
import DisplayPluginOptionsAPIProvider from './plugins/DisplayPluginOptionsAPIProvider.vue'
import ThemeOptionsAPIProvider from './plugins/ThemeOptionsAPIProvider.vue'
import { getIconForType } from '@/utils/flowyObjectIcons'
/*
|---------------------------------------------------------------------
| Main Application Component
|---------------------------------------------------------------------
*/
export default {
  components: {
    Layout,
    DisplayPluginOptionsAPIProvider,
    ThemeOptionsAPIProvider
  },
  data() {
    return {
      isRouteLoaded: false,
      visible: false,
      // GET /build-info is asked once per page load, as soon as a user is logged in
      backendBuildChecked: false
    }
  },
  head: {
    link: [
      // adds config/icons into the html head tag
      ...config.icons.map((href) => ({ rel: 'stylesheet', href }))
    ]
  },
  computed: {
    ...mapGetters('app',['currentSnackbar', 'userRoles', 'userSettings']),
    snackbar() {
      return this.currentSnackbar
    }
  },
  watch: {
    $route: {
      handler(val) {
        if (val && val.name && !this.isRouteLoaded) {
          this.isRouteLoaded = true
          this.runRouteChange()
        }
      }
    },
    snackbar: {
      handler(newVal) {
        if (newVal?.message) {
          this.visible = true
          setTimeout(this.removeSnackbar, newVal.timeout || 5000)
        } else {
          this.visible = false
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Initialize RTL mode if an RTL language is selected
    const lang = localStorage.selectedLanguage
    if (lang && RTL_LANGUAGES.includes(lang)) {
      this.$store.commit('setRTL', true)
    }

    if (auth.isLoggedIn()) {
      if (this.userRoles?.length) {
        const isUserGlobalSettingsAdminOrSuperUser = this.userRoles.some(
          (x) => x.name === 'GLOBAL_SETTING' || x.name === 'SUPER_USER'
        )

        if (isUserGlobalSettingsAdminOrSuperUser) {
          this.getFrontendSetting()
        } else {
          this.getBasicSettingsCall()
        }
      } else {
        this.getBasicSettingsCall()
      }

      this.getMfaStartingDate()
    }
  },
  created() {
    const lang = localStorage.selectedLanguage
      ? localStorage.selectedLanguage
      : 'en'

    localStorage.setItem('selectedLanguage', this.$route.params.lang || lang)

    const savedLocationsLoad = localStorage.savedLocations
      ? JSON.parse(localStorage.savedLocations)
      : []

    this.savedLocationsSave(savedLocationsLoad)

    this.checkStaticHash()
  },
  methods: {
    ...mapActions('app', ['defaultFrontend', 'savedLocationsSave', 'removeSnackbar', 'userSettingsSave', 'isOAuthSave']),
    closeSnackbar() {
      this.visible = false
      this.removeSnackbar()
    },
    runRouteChange() {
      this.$router.beforeEach((to, from, next) => {
        // After a login the first navigation asks the backend build once (#6110)
        this.checkBackendBuild()
        if (to?.name && to.name !== 'error') {
          const savedLocations = localStorage.savedLocations
            ? JSON.parse(localStorage.savedLocations)
            : []
          const savedLocationsPaths = savedLocations.map((x) => x.fullPath)

          this.savedLocationsSave(savedLocations)

          const isFromEdit = from.fullPath.split('/').includes('edit')
          const isToEdit = to.fullPath.split('/').includes('edit')
          const isFromInSaved = savedLocationsPaths.includes(from.fullPath)
            ? savedLocationsPaths.indexOf(from.fullPath)
            : -1
          const isToInSaved = savedLocationsPaths.includes(to.fullPath)
            ? savedLocationsPaths.indexOf(from.fullPath)
            : -1

          if (isFromEdit)
            this.handleEditCreate(savedLocations, from, isFromInSaved)

          if (isToEdit) this.handleEditCreate(savedLocations, to, isToInSaved)
          // eslint-disable-next-line callback-return
          next()
        } else {
          // eslint-disable-next-line callback-return
          next()
        }
        // this.checkStaticHash()
      })
    },
    iconHandler(type, subType) {
      return getIconForType(type, subType)
    },
    checkBackendBuild() {
      if (this.backendBuildChecked || !localStorage.userData) return
      this.backendBuildChecked = true

      getBuildInfo()
        .then((res) => {
          const result = res?.data?.data

          if (!result) return
          if (result.oauth) {
            this.isOAuthSave(result.oauth.enabled === true)
            localStorage.oauthEnabled = String(result.oauth.enabled === true)
          }
          if (result.git) {
            const backendVersion = `${result.git.commit?.id}-${result.git.build?.number}`
            let stored = null

            if (localStorage.backendVersion) {
              try {
                stored = JSON.parse(localStorage.backendVersion)
              } catch {
                stored = null
              }
            }
            const storedVersion = stored
              ? `${stored.commit?.id}-${stored.build?.number}`
              : null
            const isFirstLoad = !storedVersion

            localStorage.backendVersion = JSON.stringify(result.git)
            if (!isFirstLoad && storedVersion !== backendVersion) {
              window.location.reload(true)
            }
          }
        })
        .catch(() => {
          // 403 for users without SUPER_USER / FLOWY_ADMIN: keep what the browser already knows
        })
    },
    handleEditCreate(savedLocations, editObject, isInSavedIndex) {
      let savedLocationsInner = savedLocations

      const resourcesTypes = {
        translations: 'translation',
        modules: 'module',
        local: 'module',
        processes: 'process',
        credentials: 'process-credential',
        templates: 'text-template',
        cron: 'trigger-cron',
        rest: 'trigger-rest',
        ui: 'trigger-ui',
        flow: 'flow',
        mcp: 'trigger-mcp',
        validations: 'validation-rule',
        plugins: 'plugin',
        settings: 'setting',
        users: 'user',
        roles: 'role',
        tenants: 'tenant',
        tags: 'tag',
        history: 'history',
        hub: 'hub/module',
        libraries: 'library',
        storage: 'storage',
        'event-handler': 'trigger-event-handler',
        entities: 'entity',
        messaging: 'trigger-messaging',
        'static-resources': 'static-resource',
        aiAgents: 'ai-agent',
        applications: 'application',
        whatsapp: 'trigger-whatsapp',
        screen: 'screen'
      }

      const isNestedRoute = (routeName) => {
        const nestedRoutes = [
          'triggers',
          'access',
          'templates',
          'modules'
        ]

        return nestedRoutes.includes(routeName)
      }

      const isStorage = editObject.fullPath.split('/')[2] === 'storage'

      if (isInSavedIndex !== -1) {
        savedLocationsInner.sort((x, y) => {
          return x === savedLocationsInner[isInSavedIndex]
            ? -1
            : y === savedLocationsInner[isInSavedIndex]
              ? 1
              : 0
        })
        localStorage.savedLocations = JSON.stringify(savedLocationsInner)
        this.savedLocationsSave(savedLocationsInner)
      } else {
        const resourceKey = isNestedRoute(editObject.fullPath.split('/')[2])
          ? editObject.fullPath.split('/')[3]
          : editObject.fullPath.split('/')[2]

        if (resourceKey === 'hub') {
          // the hub API has no fetch-by-id - the datatable passes the module
          // name along in the route query, so the location is saved from it
          savedLocationsInner.unshift({
            fullPath: editObject.fullPath,
            name: editObject.query?.name || `# ${editObject.params.id}`,
            icon: this.iconHandler('hub')
          })
          savedLocationsInner = savedLocationsInner.filter(
            (value, index, self) =>
              index === self.findIndex((t) => t.fullPath === value.fullPath)
          )
          localStorage.savedLocations = JSON.stringify(
            savedLocationsInner.slice(0, 15)
          )
          this.savedLocationsSave(savedLocationsInner.slice(0, 15))

          return
        }

        let axiosUrl = `/api/${
          resourcesTypes[
            isNestedRoute(editObject.fullPath.split('/')[2])
              ? editObject.fullPath.split('/')[3]
              : editObject.fullPath.split('/')[2]
          ]
        }/${editObject.params.id}`

        if (editObject.query) {
          const queries = Object.keys(editObject.query).map(
            (key) => `${key}=${editObject.query[key]}`
          )

          axiosUrl += `?${queries.join('&')}`
        }

        if (axiosUrl && !axiosUrl.includes('undefined')) {
          axios.get(axiosUrl).then((res) => {
            if (res.status === 404) {
              return
            }

            if (isStorage) {
              getProcess({ id: res.data?.data?.processId })
                .then((res2) => {
                  savedLocationsInner.unshift({
                    fullPath: editObject.fullPath,
                    name: `${res2.data?.data?.name}: ${res.data?.data?.key}`,
                    icon: this.iconHandler(
                      isNestedRoute(editObject.fullPath.split('/')[2])
                        ? editObject.fullPath.split('/')[3]
                        : editObject.fullPath.split('/')[2],
                      res.data?.data?.type
                    )
                  })
                  savedLocationsInner = savedLocationsInner.filter(
                    (value, index, self) =>
                      index ===
                      self.findIndex((t) => t.fullPath === value.fullPath)
                  )
                  localStorage.savedLocations = JSON.stringify(
                    savedLocationsInner.slice(0, 15)
                  )
                  this.savedLocationsSave(savedLocationsInner.slice(0, 15))
                })
                .catch((error) => {
                  console.log(error)
                })
            } else {
              savedLocationsInner.unshift({
                fullPath: editObject.fullPath,
                name: res.data?.data?.name || res.data?.data?.resourceName,
                icon: this.iconHandler(
                  isNestedRoute(editObject.fullPath.split('/')[2])
                    ? editObject.fullPath.split('/')[3]
                    : editObject.fullPath.split('/')[2],
                  res.data?.data?.type
                )
              })
              savedLocationsInner = savedLocationsInner.filter(
                (value, index, self) =>
                  index === self.findIndex((t) => t.fullPath === value.fullPath)
              )
              localStorage.savedLocations = JSON.stringify(
                savedLocationsInner.slice(0, 15)
              )
              this.savedLocationsSave(savedLocationsInner.slice(0, 15))
            }

            savedLocationsInner = savedLocationsInner.filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.fullPath === value.fullPath)
            )
            localStorage.savedLocations = JSON.stringify(
              savedLocationsInner.slice(0, 15)
            )
            this.savedLocationsSave(savedLocationsInner.slice(0, 15))
          })
            .catch((error) => {
              console.log(error)
            })
        }
      }
    },
    getFrontendSetting() {
      getSettings({ type: 'FRONTEND' })
        .then((res) => {
          if (res.status !== 200) {
            this.getBasicSettingsCall()

            return
          }

          const defaultFe = res.data.data.find((x) => x.type === 'FRONTEND')

          if (defaultFe) {
            getSingleSetting({ id: defaultFe.id })
              .then((res2) => {
                this.defaultFrontend(res2.data.data.values)
                document.title = res2?.data?.data?.values?.environmentName ? `${res2?.data?.data?.values?.environmentName} - Flowy` : 'Flowy'
              })
              .catch((err) => {
                this.err = err
              })
          }

        })
        .catch((error) => {
          this.loading = false
          console.log(error)
        })
    },
    getBasicSettingsCall() {
      getBasicSettings()
        .then((res2) => {
          if (res2.status === 200) {
            this.defaultFrontend(res2.data.data)
            document.title = res2?.data?.data?.environmentName ? `${res2?.data?.data?.environmentName} - Flowy` : 'Flowy'
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getMfaStartingDate() {

      const roles = this.$store.getters['app/userRoles'].map(r => r.name)

      if (!roles.includes('SUPER_USER') && !roles.includes('GLOBAL_SETTING')) {
        return // Skip the call
      }
      getSettings({ type: 'ADMIN' })
        .then((res) => {
          if (res?.status === 200 && res?.data?.data) {
            const adminSettingsId = res.data.data.find((x) => x.type === 'ADMIN')?.id || null

            if (adminSettingsId) {
              getSingleSetting({ id: adminSettingsId })
                .then((res2) => {
                  const mfaStartingDate = res2.data.data.values.mfaStartingDate || null

                  this.userSettingsSave({...this.userSettings, mfaStartingDate})
                })
            }
          }
        })
    },
    checkStaticHash() {
      fetch(
        process.env.NODE_ENV === 'development'
          ? '/version.txt'
          : '/static/version.txt'
      )
        .then((r) => r.json())
        .then((staticHash) => {
          if (staticHash) {
            if (!localStorage.frontendVersion) {
              localStorage.frontendVersion = staticHash
            } else if (Number(localStorage.frontendVersion) !== staticHash) {
              localStorage.frontendVersion = staticHash
              window.location.reload(true)
            }
          }
        })
        .catch(() => {
          if (localStorage.frontendVersion !== 'null') {
            localStorage.frontendVersion = 'null'
            window.location.reload(true)
          } else {
            localStorage.frontendVersion = 'null'
          }
        })
      fetch(
        process.env.NODE_ENV === 'development'
          ? '/buildtime.txt'
          : '/static/buildtime.txt'
      )
        .then((r) => r.text())
        .then((buildtime) => {
          const lines = buildtime.split('\n')
          const timestamp = lines[0]
          const commit = lines.length > 1 ? lines[1].trim() : ''
          localStorage.frontendCommit = commit

          const year = timestamp.substring(0, 4)
          const month = timestamp.substring(4, 6)
          const day = timestamp.substring(6, 8)
          const hour = timestamp.substring(9, 11)
          const minute = timestamp.substring(11, 13)
          const second = timestamp.substring(13, 15)

          const buildtimeDate = new Date(
            year,
            Number(month) - 1,
            day,
            hour,
            minute,
            second
          ).toISOString()

          if (buildtimeDate) {
            if (!localStorage.frontendBuildtime) {
              localStorage.frontendBuildtime = buildtimeDate
            } else if (localStorage.frontendBuildtime !== buildtimeDate) {
              localStorage.frontendBuildtime = buildtimeDate
              localStorage.removeItem('preFillData')
              window.location.reload(true)
            }
          }
        })
        .catch(() => {
          localStorage.frontendCommit = 'null'
          if (localStorage.frontendBuildtime !== 'null') {
            localStorage.frontendBuildtime = 'null'
            window.location.reload(true)
          } else {
            localStorage.frontendBuildtime = 'null'
          }
        })
      // Build information moved from the public actuator endpoint to GET /build-info,
      // which needs a logged-in SUPER_USER or FLOWY_ADMIN (#6110). The login page keeps
      // showing the OAuth button from the last answer this browser has seen.
      this.isOAuthSave(localStorage.oauthEnabled === 'true')
      this.checkBackendBuild()

      let userLocal = localStorage.getItem('userData')
        ? JSON.parse(localStorage.getItem('userData'))
        : null

      const tenantId = localStorage.getItem('tenantId')

      if (userLocal) {
        getUser()
          .then((res) => {
            if (res.status === 200) {
              if (res.data.data) {
                userLocal = {
                  ...userLocal,
                  roles: res.data.data.roles,
                  primaryRole: res.data.data.primaryRole ?? null,
                  tenantId
                }
                localStorage.setItem('userData', JSON.stringify(userLocal))
                this.$store.dispatch('app/userRolesSave', res.data.data.roles)
                this.$store.dispatch('app/userPrimaryRoleSave', res.data.data.primaryRole ?? null)
              }
            }
          })
          .catch((error) => {
            console.log(error)
          })
      }
    }
  }
}
</script>
